import { useState, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { formatTimeStamp } from "../../api/callData";

const ChatDetails = ({ chat, onClose }) => {
    const [activeTab, setActiveTab] = useState('overview');
    const [copiedId, setCopiedId] = useState(null);
    const [copiedText, setCopiedText] = useState(null);
    
    const [isInfoExpanded, setIsInfoExpanded] = useState(true);
    const [isMobile, setIsMobile] = useState(false);
    
    // Check if screen is mobile on mount and when window resizes
    useEffect(() => {
        const checkIfMobile = () => {
            const isMobileView = window.innerWidth < 768; // md breakpoint in Tailwind is 768px
            setIsMobile(isMobileView);
            
            // If switching from mobile to desktop, ensure content is visible
            if (!isMobileView && !isInfoExpanded) {
                setIsInfoExpanded(true);
            }
        };
        
        checkIfMobile();
        window.addEventListener('resize', checkIfMobile);
        
        return () => {
            window.removeEventListener('resize', checkIfMobile);
        };
    }, [isInfoExpanded]);

    // const getTrendStyles = (trend) => {
    //     const styles = {
    //         improving: {
    //             textColor: "text-green-500",
    //             icon: "iconamoon:trend-up-light"
    //         },
    //         declining: {
    //             textColor: "text-error",
    //             icon: "iconamoon:trend-down-light"
    //         },
    //         stable: {
    //             textColor: "text-secondary",
    //             icon: "mdi:trending-neutral"
    //         },
    //         neutral: {
    //             textColor: "text-font-secondary",
    //             icon: "ph:empty"
    //         }
    //     };

    //     return styles[trend] || styles.neutral;
    // };

    // const getBookingStatusStyles = (status) => {
    //     const statusLower = status?.toLowerCase();
    //     if (statusLower?.includes('confirmed') || statusLower?.includes('completed')) {
    //         return { textColor: 'text-green-500', icon: 'material-symbols:check-circle-outline' };
    //     }
    //     if (statusLower?.includes('pending') || statusLower?.includes('processing')) {
    //         return { textColor: 'text-yellow-500', icon: 'material-symbols:pending-actions-outline' };
    //     }
    //     if (statusLower?.includes('cancelled') || statusLower?.includes('rejected')) {
    //         return { textColor: 'text-error', icon: 'material-symbols:cancel-outline' };
    //     }
    //     return { textColor: 'text-font-secondary', icon: 'ph:question' };
    // };

    const handleCopy = (text, type = 'id') => {
        navigator.clipboard.writeText(text);
        if (type === 'id') {
            setCopiedId(text);
            setTimeout(() => setCopiedId(null), 1000);
        } else {
            setCopiedText(text);
            setTimeout(() => setCopiedText(null), 1000);
        }
    }

    const tabs = [
        { id: 'overview', label: 'Overview', icon: 'material-symbols:info-outline' },
        { id: 'conversation', label: 'Conversation', icon: 'material-symbols:description' },
        { id: 'analytics', label: 'Analytics', icon: 'material-symbols:analytics' },
        { id: 'summary', label: 'Summary', icon: 'material-symbols:summarize' }
    ];

    const renderOverview = () => (
        <div className="w-full flex-1 flex flex-col gap-2 overflow-y-auto scrollable">
            <div className="w-full flex flex-col gap-3">
                <h3 className="subheader-textbox text-sm">Chat Information</h3>
                <div className="card-container rounded-md space-y-2 p-3">
                    <div className="text-sm">
                        <span className="font-medium capitalize text-secondary">Session ID: </span>
                        <span className="text-font-secondary">{chat.metadata.session_id || 'Not specified'}</span>
                    </div>
                    <div className="text-sm">
                        <span className="font-medium capitalize text-secondary">Conversation ID: </span>
                        <span className="text-font-secondary">{chat.conversation_id || 'Not specified'}</span>
                    </div>
                    <div className="text-sm">
                        <span className="font-medium capitalize text-secondary">Start Time: </span>
                        <span className="text-font-secondary">{formatTimeStamp(chat.metadata.timestamp) || 'Not specified'}</span>
                    </div>
                    <div className="text-sm">
                        <span className="font-medium capitalize text-secondary">Message Count: </span>
                        <span className="text-font-secondary">{chat.analytics.message_count || 'Not specified'}</span>
                    </div>
                </div>

                <h3 className="subheader-textbox text-sm">Performance Metrics</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="card-container p-3 rounded-md">
                        <div className="text-sm font-medium text-secondary mb-1">User Sentiment</div>
                        <div className={`text-base text-primary font-semibold flex items-center gap-2 ${
                            chat.analytics.user_sentiment === 'positive' ? 'text-green-500' : 
                            chat.analytics.user_sentiment === 'negative' ? 'text-error' : 'text-font-secondary'
                        }`}>
                            <Icon icon={chat.analytics.user_sentiment === 'positive' ? 'iconamoon:trend-up-light' : 
                                chat.analytics.user_sentiment === 'negative' ? 'iconamoon:trend-down-light' : 'ph:empty'} className="text-lg" />
                            {chat.analytics.user_sentiment || 'neutral'}
                        </div>
                    </div>
                    <div className="card-container p-3 rounded-md">
                        <div className="text-sm font-medium text-secondary mb-1">Success Score</div>
                        <div className={`text-base text-primary font-semibold flex items-center gap-2 ${
                            chat.evaluation.success_score >= 7 ? 'text-green-500' : 
                            chat.evaluation.success_score >= 4 ? 'text-yellow-500' : 'text-error'
                        }`}>
                            <Icon icon={chat.evaluation.success_score >= 7 ? 'fa-regular:smile' : 
                                chat.evaluation.success_score >= 4 ? 'fontisto:neutral' : 'bx:sad'} className="text-lg" />
                            {chat.evaluation.success_score >= 7 ? 'Pass' : chat.evaluation.success_score >= 4 ? 'Partial' : 'Fail'}
                        </div>
                    </div>
                    {/* <div className="card-container p-3 rounded-md">
                        <div className="text-sm font-medium text-secondary mb-1">Feedback</div>
                        <div className={`text-base text-primary font-semibold flex items-center gap-2 ${
                            chat.feedback_exists ? 'text-green-500' : 'text-font-secondary'
                        }`}>
                            <Icon icon="material-symbols:feedback-rounded" className="text-lg" />
                            {chat.feedback_exists ? 'Available' : 'None'}
                        </div>
                    </div> */}
                </div>
            </div>
        </div>
    );

    const renderConversation = () => (
        <div className="w-full h-full p-2 items-start flex flex-col gap-6 overflow-x-hidden">
            {chat.conversation && chat.conversation.length > 0 ? (
                chat.conversation.map((message, index) => (
                    <div key={index} className={`flex w-full font-medium gap-2 items-start mb-6 ${message.role === 'assistant' ? 'flex-row' : 'flex-row-reverse'}`}>
                        <div className={`p-3 rounded-md flex-shrink-0 ${message.role === 'assistant' ? 'tag-primary' : 'tag-gray'}`}>
                            <Icon icon={message.role === 'assistant' ? 'mingcute:ai-fill' : 'mdi:user'} className={`text-xl ${message.role === 'assistant' ? 'text-primary' : 'text-accent'}`} />
                        </div>
                        <div className={`flex flex-col flex-1 min-w-0 max-w-full gap-2 ${message.role === 'assistant' ? 'items-start' : 'items-end'}`}>
                            <div className={`flex gap-2 w-full ${message.role === 'assistant' ? 'flex-row items-end' : 'flex-row-reverse items-end'}`}>
                                <div className={`p-3 text-sm rounded-md break-words overflow-wrap-anywhere max-w-full ${message.role === 'assistant' ? 'tag-primary' : 'tag-gray'}`}>
                                    {message.content}
                                </div>
                                <p className="text-xs text-font-secondary flex-shrink-0 whitespace-nowrap">{message.timestamp ? message.timestamp.slice(11, 19) : ''}</p>
                            </div>
                        </div>
                    </div>
                ))
            ) : (
                <div className="flex flex-col items-center justify-center py-10 w-full text-center text-gray-400">
                    <Icon icon="mdi:chat-off-outline" className="text-5xl mb-2" />
                    <p>No conversation available for this chat.</p>
                </div>
            )}
        </div>
    );

    const renderAnalytics = () => (
        <div className="px-2 w-full h-full flex flex-col items-start">
            <div className="flex flex-col gap-4 w-full">
                <h3 className="subheader-textbox text-sm">Chat Analytics</h3>
                <div className="grid grid-cols-6 gap-x-2 gap-y-3">
                    {Object.entries(chat.analytics || {})
                        .filter(([key]) => key !== 'resolution_status' && key !== 'conversation_topics')
                        .sort(([keyA], [keyB]) => {
                            const order = {
                                'message_count': 1,
                                'user_message_count': 2,
                                'assistant_message_count': 3,
                                'user_sentiment': 4
                            };
                            return (order[keyA] || 999) - (order[keyB] || 999);
                        })
                        .map(([key, value], index) => {
                            return (
                                <div 
                                    key={key} 
                                    className={`card-container p-3 rounded-md capitalize 
                                        ${index < 3 ? 'col-span-2' : 'col-span-3'}
                                        ${index < 5 ? 'flex flex-row items-center justify-between' : ''}
                                        `}
                                >
                                    <p className="text-sm font-medium">{key.replace(/_/g, ' ')}</p>
                                    <div className="text-base text-primary font-semibold">
                                        {typeof value === 'object' ? 
                                            JSON.stringify(value) : 
                                            value?.toString() || 'Not specified'
                                        }
                                    </div>
                                </div>
                            );
                        })}
                </div>
                
                <h3 className="subheader-textbox text-sm">Success Evaluation</h3>
                <div className="card-container rounded-md font-medium p-4 gap-2 flex flex-col">
                    <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-secondary">Success Score:</span>
                        <span className={`text-base font-semibold ${
                            chat.evaluation.success_score >= 7 ? 'text-green-500' : 
                            chat.evaluation.success_score >= 4 ? 'text-yellow-500' : 'text-error'
                        }`}>
                            {chat.evaluation.success_score || 'N/A'}
                        </span>
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-secondary">Status:</span>
                        <span className="text-base font-semibold text-primary">
                            {chat.evaluation.success_score >= 7 ? 'Pass' : chat.evaluation.success_score >= 4 ? 'Partial' : 'Fail'}
                        </span>
                    </div>
                    {chat.evaluation.improvement_areas && (
                        <div className="mt-2">
                            <span className="text-sm font-medium text-secondary">Improvement Areas:</span>
                            <div className="text-sm text-font-secondary mt-1">
                                {Array.isArray(chat.evaluation.improvement_areas) ? 
                                    chat.evaluation.improvement_areas.join(', ') : 
                                    chat.evaluation.improvement_areas
                                }
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    const renderSummary = () => (
        <div className="w-full flex-1 flex flex-col gap-2 overflow-y-auto scrollable">
            <div className="w-full flex flex-col gap-3">
                <h3 className="subheader-textbox text-sm">Chat Summary</h3>
                
                {chat.summary ? (
                    <div className="flex flex-col gap-3">
                        <div className="card-container rounded-md space-y-2 p-3">
                            <div className="text-sm">
                                <span className="font-medium capitalize text-secondary">Customer Intent: </span>
                                <span className="text-font-secondary">
                                    {Array.isArray(chat.summary.customer_intent) ? 
                                        chat.summary.customer_intent.join(', ') : 
                                        chat.summary.customer_intent || 'Not specified'
                                    }
                                </span>
                            </div>
                            <div className="text-sm">
                                <span className="font-medium capitalize text-secondary">Customer Journey: </span>
                                <span className="text-font-secondary">{chat.summary.customer_journey || 'Not specified'}</span>
                            </div>
                            <div className="text-sm">
                                <span className="font-medium capitalize text-secondary">Products Discussed: </span>
                                <span className="text-font-secondary">{chat.summary.products_discussed || 'Not specified'}</span>
                            </div>
                            <div className="text-sm">
                                <span className="font-medium capitalize text-secondary">Customer Satisfaction: </span>
                                <span className="text-font-secondary">{chat.summary.customer_satisfaction || 'Not specified'}</span>
                            </div>
                        </div>
                        
                        <div className="card-container rounded-md space-y-2 p-3">
                            <div className="text-sm">
                                <span className="font-medium capitalize text-secondary">Business Opportunities: </span>
                                <span className="text-font-secondary">{chat.summary.business_opportunities || 'Not specified'}</span>
                            </div>
                            <div className="text-sm">
                                <span className="font-medium capitalize text-secondary">Key Insights: </span>
                                <span className="text-font-secondary">{chat.summary.key_insights || 'Not specified'}</span>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center py-10 w-full text-center text-gray-400">
                        <Icon icon="mdi:file-document-outline" className="text-5xl mb-2" />
                        <p>No summary available for this chat.</p>
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <div className="details-layout p-0 md:p-3 mr-2 mb-2 card-container rounded-md flex flex-col gap-4 text-sm h-full">
            <div className="flex-shrink-0 flex flex-row justify-between items-center gap-2 sm:gap-0">
                <div className="space-x-2 flex flex-row items-center gap-2 text-lg sm:text-xl font-semibold flex-shrink-0">
                    <Icon icon="material-symbols:chat" className="text-xl sm:text-2xl text-primary"></Icon>
                    Chat Details
                </div>
                <div className="flex flex-row items-center gap-2 sm:gap-3 text-font-secondary w-fit min-w-0 overflow-hidden">
                    <p className="text-xs sm:text-sm overflow-hidden text-ellipsis w-fit min-w-0 flex flex-row">
                        Id: <span className="font-semibold text-ellipsis overflow-hidden">{chat?.metadata?.session_id || 'Not specified'}</span>
                    </p>
                    <button
                        className="flex-shrink-0 text-xs sm:text-sm card-container bg-background text-accent p-1.5 sm:p-2 rounded"
                        onClick={() => handleCopy(chat?.metadata?.session_id)}
                    >
                        <Icon
                            icon={
                                copiedId === chat?.metadata?.session_id
                                ? 'mdi:check-bold'
                                : 'solar:copy-linear'
                            }
                            className="text-sm sm:text-lg"
                        />
                    </button>
                </div>
            </div>
            <div className="flex-shrink-0 card-container bg-background p-0 rounded-md">
                {/* Mobile header with toggle button */}
                <div className="flex md:hidden justify-between items-center p-2 md:p-3 w-full cursor-pointer border-b border-gray-200 dark:border-gray-700" onClick={() => setIsInfoExpanded(!isInfoExpanded)}>
                    <div className="flex items-center gap-2">
                        <Icon icon="mdi:information-outline" className="text-secondary text-lg" />
                        <span className="font-semibold">Chat Information</span>
                    </div>
                    <Icon 
                        icon={isInfoExpanded ? "mdi:chevron-up" : "mdi:chevron-down"} 
                        className="text-secondary text-lg transition-transform duration-300" 
                    />
                </div>
                
                {/* Content - Always visible on md+ screens, collapsible on mobile */}
                <div className={`
                    ${isMobile ? (isInfoExpanded ? 'max-h-96' : 'max-h-0') : 'max-h-96'} 
                    overflow-hidden transition-all duration-300 ease-in-out
                    grid grid-cols-1 sm:grid-cols-3 gap-0
                `}>
                    <div className="flex flex-col items-start gap-2 text-sm sm:text-base p-2 md:p-3 w-full">
                        <div className="flex flex-row gap-1 items-center">
                            <Icon icon="material-symbols:person" className="text-secondary text-sm sm:text-base" />
                            <span className="text-sm font-semibold">Session ID</span>
                        </div>
                        <p className="text-font-secondary text-sm sm:text-base font-semibold">
                            {chat.metadata.session_id}
                        </p>
                    </div>
                    <div className="flex flex-col items-start gap-2 text-sm sm:text-base p-2 sm:p-3 w-full">
                        <div className="flex flex-row gap-1 items-center">
                            <Icon icon="tabler:message" className="text-secondary text-sm sm:text-lg" />
                            <span className="text-sm font-semibold">Message Count</span>
                        </div>
                        <p className="text-font-secondary text-sm sm:text-base font-semibold">
                            {chat.analytics?.message_count || 'No message count available'}
                        </p>
                    </div>
                    <div className="flex flex-col items-start gap-2 text-sm sm:text-base p-2 sm:p-3 w-full">
                        <div className="flex flex-row gap-1 items-center">
                            <Icon icon="mingcute:time-duration-line" className="text-secondary text-sm sm:text-lg" />
                            <span className="text-sm font-semibold">Started at</span>
                        </div>
                        <p className="text-font-secondary text-sm sm:text-base font-semibold">
                            {chat.metadata?.timestamp ? formatTimeStamp(chat?.metadata?.timestamp) : 'No timestamp available'}
                        </p>
                    </div>
                </div>
            </div>
            <div className="flex-shrink-0 w-full flex flex-row items-center text-sm sm:text-base">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex-1 flex flex-row justify-center items-center gap-1 p-2 min-w-0 whitespace-nowrap ${
                            activeTab === tab.id ? "tickets-tab-active" : "tickets-tab-inactive"
                        }`}
                    >
                        <Icon icon={tab.icon} className={`text-lg sm:text-xl ${
                            activeTab === tab.id ? "text-primary" : ""
                        }`} />
                        <span className={`hidden sm:inline ${activeTab === tab.id ? 'font-semibold' : 'font-medium'}`}>
                            {tab.label}
                        </span>
                    </button>
                ))}
            </div>
            <div className="flex-shrink-0 flex flex-col gap-4 items-start w-full flex-1 min-h-0">
                <div className={`flex flex-row justify-center font-semibold items-center px-2 gap-1 text-lg sm:text-xl text-font-primary flex-shrink-0`}>
                    <Icon icon={tabs.find(tab => tab.id === activeTab)?.icon} className="text-xl sm:text-2xl text-secondary" />
                    {tabs.find(tab => tab.id === activeTab)?.label}
                </div>
                
                <div className="w-full flex-1 min-h-0 overflow-hidden">
                    <div className="w-full h-full overflow-y-auto scrollable">
                        {activeTab === 'overview' && renderOverview()}
                        {activeTab === 'conversation' && renderConversation()}
                        {activeTab === 'analytics' && renderAnalytics()}
                        {activeTab === 'summary' && renderSummary()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChatDetails;
